// PointsToCash.cshtml: cardholders and their points (only when the client's program has PointsToCash on).
import { money, useFeatures } from '@qor/web-shared';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi, totalPages } from '../api';
import { usePageTitle } from '../components/ConsoleLayout';
import { toQuery, useFilterForm, useQueryParams } from '../components/filters';
import { useStaff } from '../components/RequireStaff';
import { SearchSuggest } from '../components/SearchSuggest';
import { ChooseProcessor, Pagination } from '../components/ui';
import { clientOptions, effectiveProgramId } from '../scope';

const PAGE_SIZE = 20;

export function PointsToCashPage() {
  usePageTitle('Points to Cash');
  const { user, scope } = useStaff();
  // a client's own program decides; QOR staff always see the page
  const features = useFeatures(scope.internal ? null : user.email);
  const available = scope.internal || features.isEnabled('PointsToCash');
  const q = useQueryParams();
  const f = { search: q.get('search'), programId: q.get('program'), hasPoints: q.flag('has_points') };
  const page = q.page();
  const submit = useFilterForm('/points');
  const ready = features.loaded && available && !scope.needsProvider;
  const results = useQuery({
    queryKey: ['points-to-cash', scope.activeProvider, f, page],
    enabled: ready,
    queryFn: () =>
      adminApi.pointsCardholders({
        search: f.search,
        provider: scope.activeProvider,
        programId: effectiveProgramId(scope, f.programId),
        hasPoints: f.hasPoints,
        page,
        pageSize: PAGE_SIZE,
      }),
  });
  const programs = useQuery({ queryKey: ['programs'], queryFn: adminApi.programs, enabled: ready && scope.internal });
  const { internal, activeProvider, ownProgramId } = scope;
  const suggest = useCallback(
    async (text: string) =>
      (
        await adminApi.pointsCardholders({
          search: text,
          provider: activeProvider,
          programId: internal ? null : ownProgramId,
          page: 1,
          pageSize: 6,
        })
      ).items,
    [internal, activeProvider, ownProgramId],
  );

  const heading = (
    <div className="qc-page-heading">
      <h1>Points to Cash</h1>
      <p className="qc-breadcrumb">
        QOR Console <span aria-hidden="true">&rsaquo;</span> <strong>Points to Cash</strong>
      </p>
    </div>
  );
  if (!features.loaded) return heading;
  if (!available)
    return (
      <>
        {heading}
        <div className="qc-panel">
          <p className="qc-empty">
            Your partner doesn&apos;t have points-to-cash redemption enabled. Contact QOR if you believe this should be
            turned on.
          </p>
        </div>
      </>
    );
  if (scope.needsProvider)
    return (
      <>
        {heading}
        <ChooseProcessor what="to see its cardholders" />
      </>
    );

  const data = results.data;
  return (
    <>
      {heading}
      {scope.internal && (
        <p className="qc-breadcrumb" style={{ marginBottom: 16 }}>
          Showing <strong>{scope.activeProvider}</strong> cardholders only
        </p>
      )}

      {(!scope.internal || programs.isSuccess) && (
        <form className="qc-cardholder-filters" onSubmit={submit} key={toQuery(f)}>
          <SearchSuggest
            id="points-search-input"
            defaultValue={f.search ?? ''}
            placeholder="Search by name or email..."
            suggest={suggest}
            linkTo={(id) => `/points/${id}`}
          />
          {scope.internal && (
            <select name="program" aria-label="Filter by partner" defaultValue={f.programId ?? ''}>
              <option value="">All partners</option>
              {clientOptions(programs.data ?? [], scope).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              name="has_points"
              value="true"
              defaultChecked={f.hasPoints}
              style={{ width: 18, height: 18 }}
            />
            <span>Has points (&gt;0)</span>
          </label>
          <button type="submit" className="qor-button qor-button-primary">
            Filter
          </button>
          {(f.search !== null || f.programId !== null || f.hasPoints) && (
            <Link className="qc-button-ghost" to="/points">
              Clear
            </Link>
          )}
        </form>
      )}

      <div className="qc-panel">
        {!data ? null : data.items.length === 0 ? (
          <p className="qc-empty">No cardholders match these filters.</p>
        ) : (
          <>
            <table className="qc-plain-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Program</th>
                  <th>Processor</th>
                  <th>Points balance</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td>{c.email}</td>
                    <td>{c.programName}</td>
                    <td>{c.provider}</td>
                    <td>
                      <strong>{c.pointsBalance.toLocaleString('en-US')} pts</strong>
                      <div className="qc-section-hint" style={{ margin: '2px 0 0' }}>
                        &asymp; {c.cashValue === null ? '--' : `${money(c.cashValue, c.currency)} ${c.currency}`}
                      </div>
                    </td>
                    <td>
                      <Link to={`/points/${c.id}`}>Convert &rarr;</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={data.page}
              totalPages={totalPages(data)}
              totalCount={data.totalCount}
              href={(p) =>
                `/points${toQuery({ search: f.search, program: f.programId, has_points: f.hasPoints, page: p })}`
              }
            />
          </>
        )}
      </div>
    </>
  );
}
